import { Box, Stack, Typography } from '@mui/material';
import GaugeComponent from 'react-gauge-component';

import { language } from '@/ui/i18n';
import { ColorsRaw } from '@/ui/theme/colors-raw';
import { Fonts } from '@/ui/theme/fonts';

type Props = {
  score: number;
  maxScore?: number;
  textColor?: string;
  bgColor?: string;
  percentageScale?: number;
  overrideColor?: string;
  overrideLabel?: string;
};

export function GaugeScore(props: Props) {
  return (
    <Stack alignItems={'center'} spacing={2}>
      <Box position={'relative'}>
        <ScoreGauge {...props} />
        <Box position={'absolute'} top={'50%'} sx={{ width: '100%' }}>
          <ScoreInText {...props} />
        </Box>
      </Box>
    </Stack>
  );
}

function ScoreGauge({
  score,
  maxScore,
  bgColor = ColorsRaw.scoresGauge.Background,
  percentageScale = 100,
  overrideColor,
}: Props) {
  let progressValue = maxScore ? (score / maxScore) * percentageScale : 100;
  if (isNaN(progressValue)) {
    progressValue = 0;
  }
  const arcColor = overrideColor || getArcColorFromValue(progressValue);
  return (
    <GaugeComponent
      type="semicircle"
      value={progressValue}
      arc={{
        cornerRadius: 7,
        padding: 0.03,
        width: 0.075,
        subArcs: [
          {
            length: progressValue / percentageScale,
            color: arcColor,
            showTick: false,
          },
          {
            color: bgColor,
            showTick: false,
          },
        ],
      }}
      pointer={{ hide: true }}
      labels={{
        valueLabel: { hide: true },
        tickLabels: { hideMinMax: true },
      }}
    />
  );
}

function getArcColorFromValue(progress: number) {
  if (progress > 0 && progress <= 25) {
    return ColorsRaw.scoresGauge.upTo25;
  } else if (progress > 25 && progress <= 50) {
    return ColorsRaw.scoresGauge.upTo50;
  } else if (progress > 50 && progress <= 75) {
    return ColorsRaw.scoresGauge.upTo75;
  } else if (progress > 75 && progress <= 100) {
    return ColorsRaw.scoresGauge.upTo100;
  } else {
    return 'black';
  }
}

function ScoreInText({ score, maxScore, textColor, overrideLabel }: Props) {
  const valueText =
    maxScore !== undefined
      ? `${score.toLocaleString(language)} / ${maxScore.toLocaleString(language)}`
      : `${score.toLocaleString(language)}`;
  const label = overrideLabel || valueText;
  const fontSize = label.length > 10 ? '1.25rem' : '1.75rem';
  return (
    <Typography
      sx={{
        fontSize,
        fontWeight: '700',
        fontFamily: Fonts.DarkerGrotesque,
        color: textColor || ColorsRaw.basic.White,
        textAlign: 'center',
      }}
    >
      {label}
    </Typography>
  );
}
